import React, { useEffect, useState, memo, useCallback } from "react";
import axios from "axios";
import { getToken } from "../../utils/auth";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CONFIG from "../../config";

const LegalAdmin = memo(() => {
    const { t, i18n } = useTranslation();
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState("privacy"); // 'privacy' or 'terms'

    const [form, setForm] = useState({
        title: "", title_ar: "",
        content: "", content_ar: "",
        email_override: "",
        is_active: 1,
        help_title: "", help_title_ar: "",
        help_desc: "", help_desc_ar: ""
    });

    const navigate = useNavigate();
    const isArabic = i18n.language?.startsWith('ar');

    const getHeaders = useCallback(() => {
        const token = getToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    }, []);

    const fetchPages = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${CONFIG.API_URL}/api/admin/legal`, { headers: getHeaders() });
            setPages(res.data || []);
            // Set initial form based on default tab
            const initialPage = res.data.find(p => p.slug === activeTab);
            if (initialPage) {
                setForm({
                    title: initialPage.title || "",
                    title_ar: initialPage.title_ar || "",
                    content: initialPage.content || "",
                    content_ar: initialPage.content_ar || "",
                    email_override: initialPage.email_override || "",
                    is_active: initialPage.is_active,
                    help_title: initialPage.help_title || "",
                    help_title_ar: initialPage.help_title_ar || "",
                    help_desc: initialPage.help_desc || "",
                    help_desc_ar: initialPage.help_desc_ar || ""
                });
            }
        } catch (err) {
            console.error("Error fetching legal pages:", err);
        } finally { setLoading(false); }
    }, [getHeaders, activeTab]);

    useEffect(() => { fetchPages(); }, [fetchPages]);

    const handleTabChange = (slug) => {
        setActiveTab(slug);
        const page = pages.find(p => p.slug === slug);
        if (page) {
            setForm({
                title: page.title || "",
                title_ar: page.title_ar || "",
                content: page.content || "",
                content_ar: page.content_ar || "",
                email_override: page.email_override || "",
                is_active: page.is_active,
                help_title: page.help_title || "",
                help_title_ar: page.help_title_ar || "",
                help_desc: page.help_desc || "",
                help_desc_ar: page.help_desc_ar || ""
            });
        } else {
            setForm({
                title: slug === 'privacy' ? t("admin.pages.privacy.title", "Privacy Policy") :
                    slug === 'terms' ? t("admin.pages.terms.title", "Terms & Conditions") :
                        slug === 'refund' ? t("admin.pages.refund.title", "Refund Policy") :
                            slug === 'shipping' ? t("admin.pages.shipping.title", "Shipping Policy") :
                                slug === 'cookies' ? t("admin.pages.cookies.title", "Cookie Policy") : t("admin.pages.imprint.title", "Contact / Imprint"),
                title_ar: slug === 'privacy' ? "سياسة الخصوصية" :
                    slug === 'terms' ? "الشروط والأحكام" :
                        slug === 'refund' ? "سياسة الاسترجاع" :
                            slug === 'shipping' ? "سياسة الشحن" :
                                slug === 'cookies' ? "سياسة ملفات الارتباط" : "بيانات الاتصال / إخلاء المسؤولية",
                email_override: "",
                is_active: 1,
                help_title: "", help_title_ar: "",
                help_desc: "", help_desc_ar: ""
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await axios.put(`${CONFIG.API_URL}/api/admin/legal/${activeTab}`, form, { headers: getHeaders() });
            alert(t("admin.common.save_success", "Saved successfully!"));
            fetchPages();
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || t("admin.common.save_failed", "Failed to save"));
        } finally {
            setSaving(false);
        }
    };

    const wrapSelectedText = (field, tag) => {
        const textarea = document.getElementById(field);
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;
        const selectedText = text.substring(start, end);

        let before = text.substring(0, start);
        let after = text.substring(end);
        let replacement = "";

        if (tag === 'link') {
            const url = window.prompt("Enter URL:", "https://");
            if (url) replacement = `<a href="${url}" class="text-brand-primary underline transition-opacity hover:opacity-80 transition-opacity">${selectedText || 'Link'}</a>`;
            else return;
        } else if (tag === 'h3') {
            replacement = `<h3 class="text-xl font-black text-gray-900 mt-8 mb-4">${selectedText || 'Heading'}</h3>`;
        } else if (tag === 'li') {
            replacement = `\n<li class="mb-2 ml-4 list-disc">${selectedText || 'List Item'}</li>`;
        } else {
            replacement = `<${tag}>${selectedText}</${tag}>`;
        }

        const newValue = before + replacement + after;
        setForm({ ...form, [field]: newValue });

        // Restore focus and selection
        setTimeout(() => {
            textarea.focus();
            const newCursorPos = start + replacement.length;
            textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
    };

    const Toolbar = ({ field }) => (
        <div className="flex gap-2 p-2 bg-white rounded-t-2xl border-b border-gray-100 mb-0">
            <button type="button" onClick={() => wrapSelectedText(field, 'b')} title={t("admin.legal.toolbar.bold", "Bold")} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg text-xs font-black">B</button>
            <button type="button" onClick={() => wrapSelectedText(field, 'i')} title={t("admin.legal.toolbar.italic", "Italic")} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg text-xs italic font-serif">I</button>
            <button type="button" onClick={() => wrapSelectedText(field, 'h3')} title={t("admin.legal.toolbar.heading", "Heading")} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg text-[10px] font-black uppercase tracking-tighter">H3</button>
            <button type="button" onClick={() => wrapSelectedText(field, 'li')} title={t("admin.legal.toolbar.list", "List Item")} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg text-xs"><i className="fas fa-list-ul"></i></button>
            <button type="button" onClick={() => wrapSelectedText(field, 'link')} title={t("admin.legal.toolbar.link", "Link")} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg text-xs"><i className="fas fa-link"></i></button>
        </div>
    );

    return (
        <div className="max-w-6xl mx-auto my-12 p-8 bg-white rounded-[40px] shadow-2xl border border-gray-100 mt-24">
            <div className="flex flex-wrap justify-center md:justify-between items-center gap-4 md:gap-0 mb-12">
                <div>
                    <h2 className="text-4xl font-black text-gray-900 tracking-tight">
                        {t("admin.legal.title", "Legal Pages Management")}
                    </h2>
                    <p className="text-gray-500 mt-2 font-medium">
                        {t("admin.legal.subtitle", "Edit your shop's privacy policy and terms of use.")}
                    </p>
                </div>
                <button onClick={() => navigate("/admin")} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all">
                    <span className={`inline-block transition-transform ${isArabic ? "rotate-180" : ""}`}>←</span>
                    {t('admin.common.back', 'Back')}
                </button>
            </div>

            {/* Tab Navigation */}
            <div className="flex flex-wrap gap-2 mb-8 p-2 bg-gray-50 rounded-2xl w-fit">
                {["privacy", "terms", "refund", "shipping", "cookies", "imprint"].map(slug => (
                    <button
                        key={slug}
                        onClick={() => handleTabChange(slug)}
                        className={`px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${activeTab === slug ? "bg-brand-primary text-white shadow-lg shadow-brand-primary/20" : "text-gray-500 hover:bg-gray-100"}`}
                    >
                        {t(`pages.${slug}.title`, slug.charAt(0).toUpperCase() + slug.slice(1))}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="text-center py-20">
                    <i className="fas fa-spinner fa-spin text-4xl text-brand-primary"></i>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="bg-brand-primary/5 p-10 rounded-[40px] border border-brand-primary/10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                        {/* English Content */}
                        <div className="space-y-6">
                            <h3 className="text-sm font-black text-gray-400 uppercase tracking-widest ml-4">
                                {t("admin.common.english", "English Version")}
                            </h3>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.common.page_title", "Page Title")}</label>
                                <input
                                    value={form.title}
                                    onChange={e => setForm({ ...form, title: e.target.value })}
                                    className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-bold"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.common.content", "Content")}</label>
                                <div className="rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
                                    <Toolbar field="content" />
                                    <textarea
                                        id="content"
                                        value={form.content}
                                        onChange={e => setForm({ ...form, content: e.target.value })}
                                        className="bg-white border-none p-4 w-full focus:ring-2 focus:ring-brand-primary/20 font-medium h-96 resize-y"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Arabic Content */}
                        <div className="space-y-6 text-right">
                            <h3 className="text-sm font-black text-gray-400 uppercase tracking-widest mr-4">
                                {t("admin.common.arabic", "Arabic Version")}
                            </h3>
                            <div className="space-y-2 text-right">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 mr-4">{t("admin.common.page_title_ar", "Page Title (AR)")}</label>
                                <input
                                    value={form.title_ar}
                                    onChange={e => setForm({ ...form, title_ar: e.target.value })}
                                    dir="rtl"
                                    className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-bold"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 mr-4">{t("admin.common.content_ar", "Content (AR)")}</label>
                                <div className="rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
                                    <Toolbar field="content_ar" />
                                    <textarea
                                        id="content_ar"
                                        value={form.content_ar}
                                        onChange={e => setForm({ ...form, content_ar: e.target.value })}
                                        dir="rtl"
                                        className="bg-white border-none p-4 w-full focus:ring-2 focus:ring-brand-primary/20 font-medium h-96 resize-y"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Support & Help Section */}
                    <div className="mt-12 pt-8 border-t border-brand-primary/10">
                        <h4 className="text-sm font-black text-brand-primary uppercase tracking-widest mb-6 flex items-center gap-2">
                            <i className="fas fa-question-circle"></i> {t("admin.legal.support_section", "Support & Help Section")}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {/* English Support */}
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.legal.help_title", "Help Section Title (EN)")}</label>
                                    <input
                                        value={form.help_title}
                                        onChange={e => setForm({ ...form, help_title: e.target.value })}
                                        placeholder={activeTab === 'privacy' ? "Need help with your data?" : "Have questions?"}
                                        className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-bold"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">{t("admin.legal.help_desc", "Help Section Description (EN)")}</label>
                                    <textarea
                                        value={form.help_desc}
                                        onChange={e => setForm({ ...form, help_desc: e.target.value })}
                                        placeholder={activeTab === 'privacy' ? "Our support team is here for you." : "Contact us for any clarifications about our terms."}
                                        className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-medium h-24 resize-none"
                                    />
                                </div>
                            </div>

                            {/* Arabic Support */}
                            <div className="space-y-4 text-right">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 mr-4">{t("admin.legal.help_title_ar", "Help Section Title (AR)")}</label>
                                    <input
                                        value={form.help_title_ar}
                                        onChange={e => setForm({ ...form, help_title_ar: e.target.value })}
                                        placeholder={activeTab === 'privacy' ? "هل تحتاج مساعدة في بياناتك؟" : "لديك أسئلة؟"}
                                        dir="rtl"
                                        className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-bold"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 mr-4">{t("admin.legal.help_desc_ar", "Help Section Description (AR)")}</label>
                                    <textarea
                                        value={form.help_desc_ar}
                                        onChange={e => setForm({ ...form, help_desc_ar: e.target.value })}
                                        placeholder={activeTab === 'privacy' ? "فريق الدعم لدينا هنا من أجلك." : "اتصل بنا لأي استفسارات حول شروطنا."}
                                        dir="rtl"
                                        className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-medium h-24 resize-none"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-end mt-8">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">
                                {t("admin.legal.email_override", "Support Email Override (Optional)")}
                            </label>
                            <input
                                value={form.email_override}
                                onChange={e => setForm({ ...form, email_override: e.target.value })}
                                placeholder="support@yourbrand.com"
                                className="bg-white border-none p-4 w-full rounded-2xl focus:ring-2 focus:ring-brand-primary/20 shadow-sm font-medium"
                            />
                            <p className="text-[10px] text-gray-400 ml-4 italic">
                                {t("admin.legal.email_desc", "Leave blank to use the default branding email.")}
                            </p>
                        </div>
                        <div className="flex items-center gap-4 mb-4 ml-4">
                            <input
                                type="checkbox"
                                id="isActive"
                                checked={form.is_active === 1}
                                onChange={e => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })}
                                className="w-6 h-6 accent-brand-primary"
                            />
                            <label htmlFor="isActive" className="text-sm font-black text-gray-700 uppercase tracking-widest">
                                {t("admin.common.is_active", "Page is Active")}
                            </label>
                        </div>
                    </div>

                    <div className="mt-12 flex gap-4">
                        <button
                            type="submit"
                            disabled={saving}
                            className="bg-brand-primary text-white px-12 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-brand-primary/20 hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-2"
                        >
                            {saving && <i className="fas fa-spinner fa-spin"></i>}
                            {saving ? t("admin.common.saving", "Saving...") : t("admin.common.save_changes", "Save Changes")}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
});

export default LegalAdmin;
